
import { PerceptiveSystemStat } from '@/hooks/usePerceptiveSystemStats';

export interface CharacterClass {
  name: string;
  description: string;
  icon: string;
  primaryStats: number[];
}

const CHARACTER_CLASSES: CharacterClass[] = [
  {
    name: "Mystic Scholar",
    description: "Master of thought and knowledge",
    icon: "📚",
    primaryStats: [4, 6] // Thought, Social
  },
  {
    name: "Shadow Walker",
    description: "Silent observer of all things",
    icon: "🥷",
    primaryStats: [1, 5] // Sound, Environment
  },
  {
    name: "Battle Dancer",
    description: "Warrior of movement and form",
    icon: "⚔️",
    primaryStats: [2, 3] // Physical, Movement
  },
  {
    name: "Elemental Guardian",
    description: "Protector attuned to all realms",
    icon: "🛡️",
    primaryStats: [1, 2, 5] // Sound, Physical, Environment
  },
  {
    name: "Mind Weaver",
    description: "Architect of consciousness",
    icon: "🧠",
    primaryStats: [4] // Thought dominant
  },
  {
    name: "Wandering Sage",
    description: "Balanced seeker of truth",
    icon: "🎭",
    primaryStats: [] // Balanced across all
  }
];

export const useCharacterClass = (stats: PerceptiveSystemStat[]) => {
  const getCharacterClass = (): CharacterClass => {
    if (stats.length === 0) {
      return CHARACTER_CLASSES[5]; // Default to Wandering Sage
    }

    // Calculate dominant stats (level 5+)
    const dominantStats = stats
      .filter(stat => stat.level >= 5)
      .map(stat => stat.systemId)
      .sort();

    // Find best matching class
    for (const characterClass of CHARACTER_CLASSES) {
      if (characterClass.primaryStats.length === 0) continue; // Skip balanced class for now
      
      const matchingStats = characterClass.primaryStats.filter(statId => 
        dominantStats.includes(statId)
      );
      
      if (matchingStats.length >= Math.min(2, characterClass.primaryStats.length)) {
        return characterClass;
      }
    }

    // Check for single stat dominance
    const highestStat = stats.reduce((max, stat) => 
      stat.level > max.level ? stat : max
    );

    if (highestStat.level >= 7) {
      const singleStatClass = CHARACTER_CLASSES.find(cls => 
        cls.primaryStats.length === 1 && cls.primaryStats[0] === highestStat.systemId
      );
      if (singleStatClass) return singleStatClass;
    }

    // Default to balanced class
    return CHARACTER_CLASSES[5];
  };

  const getTotalLevel = (): number => {
    return stats.reduce((sum, stat) => sum + stat.level, 0);
  };

  const getTitle = (): string => {
    const totalLevel = getTotalLevel();
    
    if (totalLevel >= 50) return "Grandmaster";
    if (totalLevel >= 40) return "Master";
    if (totalLevel >= 30) return "Expert";
    if (totalLevel >= 20) return "Adept";
    if (totalLevel >= 10) return "Apprentice";
    return "Novice";
  };

  return {
    characterClass: getCharacterClass(),
    totalLevel: getTotalLevel(),
    title: getTitle()
  };
};
